import React, { useEffect, useMemo, useState } from "react";
import { adminApi, formatApiError, getToken } from "@/lib/api";
import { Upload, Trash2, Loader2, Image as ImageIcon, Music, CheckCircle2, XCircle, Clock, Download } from "lucide-react";
import { toast } from "sonner";
import { formatDateTimeLong } from "@/lib/date";

const KIND_META = {
  photobooth_template: {
    label: "Template photobooth",
    icon: ImageIcon,
    accept: "image/png,image/jpeg,image/webp,application/pdf",
    hint: "JPG · PNG · WEBP · PDF · 40 Mo max",
  },
  audio_maquette: {
    label: "Maquette audio (ouverture de bal)",
    icon: Music,
    accept: "audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/mp4,audio/aac,audio/x-m4a,audio/m4a,application/zip,application/x-zip-compressed",
    hint: "MP3 · WAV · M4A · ZIP (auto-décompressé) · 150 Mo max",
  },
};

const STATUS_BADGE = {
  pending:  { label: "En attente", cls: "text-[#DCB457] border-[#DCB457]/50 bg-[#DCB457]/10", Icon: Clock },
  approved: { label: "Approuvé",   cls: "text-emerald-300 border-emerald-400/50 bg-emerald-500/10", Icon: CheckCircle2 },
  rejected: { label: "À revoir",   cls: "text-red-300 border-red-400/50 bg-red-500/10", Icon: XCircle },
};

export default function AdminValidations() {
  const [clients, setClients] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [filter, setFilter] = useState("");
  const [kind, setKind] = useState("photobooth_template");
  const [label, setLabel] = useState("");
  const [uploading, setUploading] = useState(false);
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);

  useEffect(() => {
    adminApi.users().then((u) => setClients(u.filter((x) => x.role === "client"))).catch(() => {});
  }, []);

  const reload = () => {
    if (!selectedUserId) { setItems([]); return; }
    setLoadingItems(true);
    adminApi.validationsList(selectedUserId)
      .then(setItems)
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoadingItems(false));
  };
  useEffect(reload, [selectedUserId]);

  const filteredClients = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return clients;
    return clients.filter((u) =>
      [u.email, u.prenom, u.nom, u.name].filter(Boolean).some((v) => String(v).toLowerCase().includes(f))
    );
  }, [clients, filter]);

  const currentClient = clients.find((x) => x.id === selectedUserId);
  const clientLabel = (u) => {
    if (!u) return "";
    const name = [u.prenom, u.nom].filter(Boolean).join(" ").trim() || u.name || "—";
    return `${name} · ${u.email}`;
  };

  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!selectedUserId) { toast.error("Sélectionnez d'abord un client."); return; }
    setUploading(true);
    try {
      await adminApi.validationUpload({ user_id: selectedUserId, kind, label, file });
      toast.success("Envoyé — le client va être notifié (email + notification in-app).");
      setLabel("");
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setUploading(false); }
  };

  const delItem = async (id) => {
    if (!window.confirm("Supprimer cet élément ? Le client ne pourra plus le voir.")) return;
    try {
      await adminApi.validationDelete(id);
      toast.success("Supprimé.");
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadFile = async (item) => {
    try {
      const token = getToken();
      const r = await fetch(adminApi.validationFileUrl(item.id), { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = item.filename || item.id; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Impossible de télécharger le fichier.");
    }
  };

  const meta = KIND_META[kind];

  return (
    <div data-testid="admin-validations">
      <header className="mb-8">
        <p className="eyebrow">Validations client</p>
        <h1 className="font-serif-luxe text-4xl md:text-5xl mt-2">
          Templates & <span className="italic gold-text">maquettes.</span>
        </h1>
        <p className="text-white/55 text-sm mt-3 max-w-2xl">
          Envoyez à un client un template photobooth (image/PDF) ou une maquette audio d&apos;ouverture de bal (MP3/WAV/M4A/ZIP).
          Le client sera notifié par <b>email</b>, <b>cloche in-app</b> et <b>push web PWA</b>, puis pourra valider ou demander une modification depuis son espace.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Client picker */}
        <aside className="lg:col-span-4">
          <div className="card-luxe p-5">
            <label className="text-[11px] uppercase tracking-[0.22em] text-white/55 mb-2 block">Rechercher un client</label>
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              data-testid="adv-filter"
              placeholder="Nom, prénom ou email…"
              className="w-full bg-black/60 border border-[#DCB457]/25 px-3 py-2 text-sm focus:border-[#DCB457] outline-none"
            />
            <div className="mt-4 max-h-[500px] overflow-y-auto border border-[#DCB457]/15 divide-y divide-[#DCB457]/10" data-testid="adv-clients-list">
              {filteredClients.length === 0 && <p className="p-4 text-sm text-white/45 italic">Aucun client.</p>}
              {filteredClients.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setSelectedUserId(c.id)}
                  data-testid={`adv-select-${c.id}`}
                  className={`w-full text-left px-4 py-3 text-sm hover:bg-[#DCB457]/5 transition ${selectedUserId === c.id ? "bg-[#DCB457]/10 border-l-2 border-l-[#DCB457]" : ""}`}
                >
                  <p className="font-serif-luxe">{[c.prenom, c.nom].filter(Boolean).join(" ").trim() || c.name || "—"}</p>
                  <p className="text-[11px] text-white/50 truncate">{c.email}</p>
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* Right column */}
        <section className="lg:col-span-8 min-w-0">
          {!selectedUserId ? (
            <div className="card-luxe p-10 text-center">
              <ImageIcon size={30} strokeWidth={1} className="text-[#DCB457] mx-auto mb-3" />
              <p className="text-white/60 text-sm">Sélectionnez un client à gauche pour lui envoyer un template ou une maquette audio.</p>
            </div>
          ) : (
            <>
              {/* Upload card */}
              <div className="card-luxe p-6 mb-6" data-testid="adv-upload-card">
                <p className="eyebrow mb-2">Client sélectionné</p>
                <p className="font-serif-luxe text-xl">{clientLabel(currentClient)}</p>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
                  {Object.entries(KIND_META).map(([k, m]) => {
                    const Icon = m.icon;
                    return (
                      <button
                        key={k}
                        type="button"
                        onClick={() => setKind(k)}
                        data-testid={`adv-kind-${k}`}
                        className={`text-left p-4 border transition ${
                          kind === k
                            ? "border-[#DCB457]/60 bg-[#DCB457]/10"
                            : "border-[#DCB457]/15 hover:border-[#DCB457]/40"
                        }`}
                      >
                        <Icon size={18} strokeWidth={1.3} className="text-[#DCB457]" />
                        <p className="font-serif-luxe text-base mt-2">{m.label}</p>
                        <p className="text-[11px] text-white/40 mt-1">{m.hint}</p>
                      </button>
                    );
                  })}
                </div>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                  <div className="md:col-span-2">
                    <label className="text-[11px] uppercase tracking-[0.22em] text-white/55 mb-2 block">Libellé (facultatif)</label>
                    <input
                      value={label}
                      onChange={(e) => setLabel(e.target.value)}
                      data-testid="adv-label"
                      placeholder="ex. Template Mariage 12 juillet 2026 — Version 1"
                      className="w-full bg-black/60 border border-[#DCB457]/25 px-3 py-2 text-sm focus:border-[#DCB457] outline-none"
                    />
                  </div>
                  <label
                    data-testid="adv-upload-btn"
                    className={`btn-gold inline-flex items-center justify-center gap-2 cursor-pointer ${uploading ? "opacity-60 pointer-events-none" : ""}`}
                  >
                    {uploading ? (
                      <><Loader2 size={14} className="animate-spin" /> Envoi…</>
                    ) : (
                      <><Upload size={14} strokeWidth={1.5} /> Envoyer au client</>
                    )}
                    <input
                      type="file"
                      accept={meta.accept}
                      onChange={onFileSelected}
                      className="hidden"
                      data-testid="adv-upload-input"
                    />
                  </label>
                </div>
                <p className="text-[11px] text-white/40 mt-3">
                  Formats acceptés pour <b>{meta.label}</b> : {meta.hint}. Le client reçoit immédiatement un email, une notification dans son espace + un push web.
                </p>
              </div>

              {/* History for this client */}
              <div className="card-luxe p-6">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="font-serif-luxe text-xl">Historique ({items.length})</h2>
                </div>
                {loadingItems ? (
                  <p className="text-white/55 text-sm italic flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Chargement…</p>
                ) : items.length === 0 ? (
                  <p className="text-white/55 text-sm italic">Aucun élément envoyé à ce client pour le moment.</p>
                ) : (
                  <ul className="space-y-3" data-testid="adv-history">
                    {items.map((v) => {
                      const badge = STATUS_BADGE[v.status] || STATUS_BADGE.pending;
                      const KindIcon = KIND_META[v.kind]?.icon || ImageIcon;
                      return (
                        <li key={v.id} className="border border-[#DCB457]/15 p-4 flex items-start gap-4" data-testid={`adv-item-${v.id}`}>
                          <KindIcon size={18} strokeWidth={1.3} className="text-[#DCB457] shrink-0 mt-1" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                              <p className="font-serif-luxe text-base truncate">{v.label || KIND_META[v.kind]?.label || v.kind}</p>
                              <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] border px-2.5 py-1 ${badge.cls}`}>
                                <badge.Icon size={11} strokeWidth={1.5} /> {badge.label}
                              </span>
                            </div>
                            <p className="text-[11px] text-white/45 mt-1">
                              {v.filename} · {(v.size / 1024).toFixed(0)} Ko · déposé le {formatDateTimeLong(v.created_at) || v.created_at?.slice(0, 16).replace("T", " ")}
                            </p>
                            {v.client_comment && (
                              <p className="text-white/70 text-sm italic mt-2 border-l-2 border-[#DCB457]/40 pl-3">« {v.client_comment} »</p>
                            )}
                            <div className="mt-3 flex gap-3">
                              <button
                                type="button"
                                onClick={() => downloadFile(v)}
                                data-testid={`adv-download-${v.id}`}
                                className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-white/70 hover:text-[#DCB457]"
                              >
                                <Download size={12} strokeWidth={1.5} /> Télécharger
                              </button>
                              <button
                                type="button"
                                onClick={() => delItem(v.id)}
                                data-testid={`adv-delete-${v.id}`}
                                className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-red-300 hover:text-red-400"
                              >
                                <Trash2 size={12} strokeWidth={1.5} /> Supprimer
                              </button>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
